import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertTriangle, Clock, XCircle, Eye, Download, ChevronUp, ChevronDown, Filter } from 'lucide-react';
import { SiteData } from '../types';
import * as XLSX from 'xlsx';
import { isValidDate, formatTableDate } from '../utils/dateUtils';

interface SitesAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  siteData: SiteData[];
  onSiteClick: (site: SiteData) => void;
}

export const SitesAnalysisModal: React.FC<SitesAnalysisModalProps> = ({
  isOpen,
  onClose,
  siteData,
  onSiteClick
}) => {
  // State for region filter
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  
  // State for sorting each table
  const [sortStates, setSortStates] = useState<{
    [key: string]: { column: string; direction: 'asc' | 'desc' }
  }>({
    renewalRisk: { column: '', direction: 'asc' },
    expiredActive: { column: '', direction: 'asc' },
    inactive: { column: '', direction: 'asc' }
  });

  // Get available regions for filter - use country/region columns
  const uniqueRegions = useMemo(() => {
    const regionSet = new Set<string>();
    
    siteData.forEach(site => {
      if (site.country === 'United States') {
        regionSet.add('North America');
        // Add US subregions (from region column)
        if (site.region) {
          regionSet.add(`${site.region} (US)`);
        }
      } else {
        // For non-US sites, use region as-is
        if (site.region) {
          regionSet.add(site.region);
        }
      }
    });
    
    return Array.from(regionSet).sort();
  }, [siteData]);

  // Filtered data based on selected regions
  const filteredData = useMemo(() => {
    if (selectedRegions.length === 0) return siteData;
    
    return siteData.filter(site => {
      return selectedRegions.some(selectedRegion => {
        if (selectedRegion === 'North America') {
          return site.country === 'United States';
        }
        
        // US subregion match (format: "RegionName (US)")
        if (selectedRegion.includes('(US)')) {
          const subRegionName = selectedRegion.replace(' (US)', '');
          return site.country === 'United States' && site.region === subRegionName;
        }
        
        // For non-US sites, match against region column
        return site.region === selectedRegion;
      });
    });
  }, [siteData, selectedRegions]);

  // Helper function to sort data
  const sortData = (data: SiteData[], column: string, direction: 'asc' | 'desc') => {
    return [...data].sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (column) {
        case 'siteName':
          aValue = a.siteName;
          bValue = b.siteName;
          break;
        case 'customer':
          aValue = a.customer;
          bValue = b.customer;
          break;
        case 'region':
          aValue = a.region;
          bValue = b.region;
          break;
        case 'status':
          aValue = a.isOnline ? 'Online' : 'Offline';
          bValue = b.isOnline ? 'Online' : 'Offline';
          break;
        case 'lastUsage':
          aValue = isValidDate(a.lastUsage) ? new Date(a.lastUsage) : new Date(0);
          bValue = isValidDate(b.lastUsage) ? new Date(b.lastUsage) : new Date(0);
          break;
        case 'licenseExpiration':
          aValue = isValidDate(a.siteLicenseExpirationInML) ? new Date(a.siteLicenseExpirationInML) : new Date(0);
          bValue = isValidDate(b.siteLicenseExpirationInML) ? new Date(b.siteLicenseExpirationInML) : new Date(0);
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Handle sort
  const handleSort = (tableKey: string, column: string) => {
    setSortStates(prev => {
      const currentState = prev[tableKey];
      const newDirection = currentState.column === column && currentState.direction === 'asc' ? 'desc' : 'asc';
      
      return {
        ...prev,
        [tableKey]: { column, direction: newDirection }
      };
    });
  };

  // Toggle region selection
  const toggleRegion = (region: string) => {
    setSelectedRegions(prev => 
      prev.includes(region) 
        ? prev.filter(r => r !== region)
        : [...prev, region]
    );
  };

  // Clear region filter
  const clearRegionFilter = () => {
    setSelectedRegions([]);
  };
  // Helper function to check if site has no activity in X months
  const hasNoActivityInMonths = (site: SiteData, months: number) => {
    if (!isValidDate(site.lastUsage)) return true; // If no valid date, consider it as no activity
    const lastUsage = new Date(site.lastUsage);
    const monthsAgo = new Date();
    monthsAgo.setMonth(monthsAgo.getMonth() - months);
    return lastUsage < monthsAgo;
  };

  // Helper function to check if site expires in next X months
  const expiresInNextMonths = (site: SiteData, months: number) => {
    if (!isValidDate(site.siteLicenseExpirationInML)) return false;
    const expiration = new Date(site.siteLicenseExpirationInML);
    const now = new Date();
    const futureDate = new Date();
    futureDate.setMonth(futureDate.getMonth() + months);
    return expiration > now && expiration <= futureDate;
  };

  // Helper function to check if site is expired
  const isExpired = (site: SiteData) => {
    if (!isValidDate(site.siteLicenseExpirationInML)) return false;
    const expiration = new Date(site.siteLicenseExpirationInML);
    const now = new Date();
    return expiration < now;
  };

  // Bucket 1: Sites with no activity in last 6 months + expiration in next 2 months
  const renewalRiskSites = filteredData.filter(site => 
    hasNoActivityInMonths(site, 6) && expiresInNextMonths(site, 2)
  );

  // Bucket 2: Expired sites that are still online
  const expiredActiveSites = filteredData.filter(site => 
    isExpired(site) && site.isOnline
  );

  // Bucket 3: Sites with no activity in last 12 months
  const inactiveSites = filteredData.filter(site => 
    hasNoActivityInMonths(site, 12)
  );

  // Prepare export data function (excluding session time columns)
  const prepareExportData = (sites: SiteData[]) => 
    sites.map(site => ({
      'Site Name': site.siteName,
      'Customer': site.customer,
      'Country': site.country,
      'Region': site.region,
      'Status': site.isOnline ? 'Online' : 'Offline',
      'Site Type': site.siteType,
      'ML Version': site.mlVersion,
      'DB Version': site.dbVersion,
      'Install Date': formatTableDate(site.siteInstallDate, 'N/A'),
      'License Expiration': formatTableDate(site.siteLicenseExpirationInML, 'N/A'),
      'Start Usage': formatTableDate(site.startUsage, 'N/A'),
      'Last Usage': formatTableDate(site.lastUsage, 'N/A'),
      'Months In Use': site.monthsInUse,
      'Used Last Month': site.usedLastMonth ? 'Yes' : 'No',
      'Simulators Connected': site.simulatorsConnected,
      'Registered Users': site.registeredUsers,
      'Active Users': site.activeUsers,
      'Inactive Users': site.inactiveUsers,
      'Sessions Last Month': site.numSessionsLastMonth,
      'Sessions on Server': site.numSessionsOnServer,
      'Sessions Last 12 Months': site.numSessionsOnServerLast12Month,
      'Active Users (12 Months)': site.activeUsers12Months
    }));

  // Export individual bucket function
  const handleExportBucket = (sites: SiteData[], bucketName: string) => {
    if (sites.length === 0) return;
    
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(prepareExportData(sites));
    XLSX.utils.book_append_sheet(wb, ws, bucketName);
    
    const fileName = `${bucketName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Export function for all analysis data
  const handleExportAnalysis = () => {
    // Create workbook
    const wb = XLSX.utils.book_new();
    
    // Create worksheets for each category
    if (renewalRiskSites.length > 0) {
      const ws1 = XLSX.utils.json_to_sheet(prepareExportData(renewalRiskSites));
      XLSX.utils.book_append_sheet(wb, ws1, 'Renewal Risk Sites');
    }
    
    if (expiredActiveSites.length > 0) {
      const ws2 = XLSX.utils.json_to_sheet(prepareExportData(expiredActiveSites));
      XLSX.utils.book_append_sheet(wb, ws2, 'Admin Required Sites');
    }
    
    if (inactiveSites.length > 0) {
      const ws3 = XLSX.utils.json_to_sheet(prepareExportData(inactiveSites));
      XLSX.utils.book_append_sheet(wb, ws3, 'Long-term Inactive Sites');
    }
    
    // Create summary sheet
    const summaryData = [
      { Category: 'Renewal Risk Sites', Count: renewalRiskSites.length, Description: 'Sites with no activity in last 6 months + expiring in next 2 months' },
      { Category: 'Admin Required Sites', Count: expiredActiveSites.length, Description: 'Expired sites that are still active and need admin update' },
      { Category: 'Long-term Inactive Sites', Count: inactiveSites.length, Description: 'Sites with no activity in last 12 months' }
    ];
    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');
    
    // Save file
    const fileName = `Sites_Analysis_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const renderSiteTable = (sites: SiteData[], title: string, icon: React.ReactNode, guidance: string, badgeVariant: 'destructive' | 'secondary' | 'outline', tableKey: string) => {
    // Apply sorting to the sites data
    const sortState = sortStates[tableKey];
    const sortedSites = sortState.column ? sortData(sites, sortState.column, sortState.direction) : sites;

    // Helper to render sortable column header
    const renderSortableHeader = (label: string, columnKey: string) => (
      <TableHead 
        className="cursor-pointer hover:bg-muted/50 select-none"
        onClick={() => handleSort(tableKey, columnKey)}
      >
        <div className="flex items-center gap-1">
          {label}
          {sortState.column === columnKey && (
            sortState.direction === 'asc' ? 
            <ChevronUp className="w-4 h-4" /> : 
            <ChevronDown className="w-4 h-4" />
          )}
        </div>
      </TableHead>
    );

    return (
      <Card className="mb-6">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {icon}
              <h3 className="text-lg font-semibold">{title}</h3>
              <Badge variant={badgeVariant}>{sites.length} sites</Badge>
            </div>
            {sites.length > 0 && (
              <Button
                onClick={() => handleExportBucket(sites, title)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
          </div>
          <p className="text-muted-foreground text-sm">{guidance}</p>
        </div>
        {sites.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {renderSortableHeader("Site Name", "siteName")}
                  {renderSortableHeader("Customer", "customer")}
                  {renderSortableHeader("Region", "region")}
                  {renderSortableHeader("Status", "status")}
                  {renderSortableHeader("Last Usage", "lastUsage")}
                  {renderSortableHeader("License Expiration", "licenseExpiration")}
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedSites.map((site) => (
                  <TableRow key={site.siteName} className="hover:bg-muted/50">
                    <TableCell className="font-medium">{site.siteName}</TableCell>
                    <TableCell>{site.customer}</TableCell>
                    <TableCell>{site.region}</TableCell>
                    <TableCell>
                      <Badge variant={site.isOnline ? "default" : "destructive"}>
                        {site.isOnline ? 'Online' : 'Offline'}
                      </Badge>
                    </TableCell>
                    <TableCell>{new Date(site.lastUsage).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <span className={isExpired(site) ? 'text-destructive font-medium' : ''}>
                        {new Date(site.siteLicenseExpirationInML).toLocaleDateString()}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button
                        onClick={() => onSiteClick(site)}
                        variant="outline"
                        size="sm"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="p-6 text-center text-muted-foreground">
            No sites found in this category
          </div>
        )}
      </Card>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <div className="flex items-center justify-between mb-4">
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-destructive" />
              Sites Analysis - Risk Assessment
            </DialogTitle>
            <div className="flex items-center gap-3">
              {/* Region Filter */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center gap-2">
                    <Filter className="h-4 w-4" />
                    Region Filter
                    {selectedRegions.length > 0 && (
                      <Badge variant="secondary" className="ml-1">
                        {selectedRegions.length}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-0 bg-background border shadow-lg" align="end">
                  <div className="p-3 border-b">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm">Filter by Region</h4>
                      {selectedRegions.length > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearRegionFilter}
                          className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="max-h-60 overflow-auto">
                    {uniqueRegions.map((region) => (
                      <div key={region} className="flex items-center space-x-2 px-3 py-2 hover:bg-muted/50">
                        <Checkbox
                          id={region}
                          checked={selectedRegions.includes(region)}
                          onCheckedChange={() => toggleRegion(region)}
                        />
                        <label htmlFor={region} className="text-sm cursor-pointer flex-1">
                          {region}
                        </label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              
              <Button
                onClick={handleExportAnalysis}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Download className="h-4 w-4" />
                Export Analysis
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card className="p-4 border-destructive/50">
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-semibold">Renewal Risk</span>
              </div>
              <p className="text-2xl font-bold mt-2">{renewalRiskSites.length}</p>
              <p className="text-sm text-muted-foreground">Sites at risk of not renewing</p>
            </Card>
            
            <Card className="p-4 border-secondary/50">
              <div className="flex items-center gap-2 text-secondary">
                <XCircle className="w-5 h-5" />
                <span className="font-semibold">Admin Required</span>
              </div>
              <p className="text-2xl font-bold mt-2">{expiredActiveSites.length}</p>
              <p className="text-sm text-muted-foreground">Expired but active sites</p>
            </Card>
            
            <Card className="p-4 border-muted">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="w-5 h-5" />
                <span className="font-semibold">Investigation Needed</span>
              </div>
              <p className="text-2xl font-bold mt-2">{inactiveSites.length}</p>
              <p className="text-sm text-muted-foreground">Long-term inactive sites</p>
            </Card>
          </div>

          {/* Detailed Tables */}
          {renderSiteTable(
            renewalRiskSites,
            "Renewal Risk Sites",
            <AlertTriangle className="w-5 h-5 text-destructive" />,
            "Sites with no activity in the last 6 months and expiring within 2 months. Reach out to the customer and see why they are not using the cloud site. Renewal in risk.",
            "destructive",
            "renewalRisk"
          )}

          {renderSiteTable(
            expiredActiveSites,
            "Administration Update Required",
            <XCircle className="w-5 h-5 text-secondary" />,
            "Expired sites that are still active (online) and need administration update - to update the expiration date, or to shut it down.",
            "secondary",
            "expiredActive"
          )}

          {renderSiteTable(
            inactiveSites,
            "Long-term Inactive Sites",
            <Clock className="w-5 h-5 text-muted-foreground" />,
            "Sites with no activity at all in the last 12 months. Reach out to the customer and understand why they are not using the site - whether the systems are not connected and they are working offline, or the systems are connected but no one is using it.",
            "outline",
            "inactive"
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};